import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crosshair, NotePencil, Trash, ArrowSquareOut, Check, X, TrendUp, TrendDown, BellRinging, SortAscending } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Formato "hace X" en español para el timestamp de creación del nivel.
// Devuelve valores compactos aptos para chips: "hace 2 min", "hace 3 h",
// "hace 5 d", "hace 2 mes", "hace 1 año".  Umbrales estilo iOS/Twitter.
const formatRelativeTime = (isoString) => {
  if (!isoString) return '';
  const then = new Date(isoString).getTime();
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'ahora';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'hace segundos';
  const min = Math.floor(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr} h`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `hace ${d} d`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `hace ${mo} mes${mo === 1 ? '' : 'es'}`;
  const yr = Math.floor(d / 365);
  return `hace ${yr} año${yr === 1 ? '' : 's'}`;
};

const MarkedLevelsPanel = ({ onTickerSelect, enrichedMarkets = [] }) => {
  const [levels, setLevels] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTicker, setEditingTicker] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  // Feb 2026: Ordenación configurable del panel de niveles marcados.
  // Opciones: 'date' (más reciente primero), 'ticker' (A→Z), 'distance' (más cercano al precio actual primero).
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    // Feb 2026: Fetches desacoplados con allSettled — si /level-events falla
    // (por ejemplo, 503 temporal del scheduler), los niveles marcados aún se
    // renderizan correctamente en lugar de dejar el panel vacío.
    Promise.allSettled([
      axios.get(`${API}/user/marked-levels`, { headers: getAuthHeaders() }),
      axios.get(`${API}/user/level-events`, { headers: getAuthHeaders() })
    ])
      .then(([lRes, eRes]) => {
        if (lRes.status === 'fulfilled') {
          setLevels(lRes.value.data?.marked_levels || []);
        } else {
          toast.error('No se pudieron cargar tus niveles');
        }
        if (eRes.status === 'fulfilled') {
          setEvents((eRes.value.data?.level_events || []).slice().reverse());
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const deleteEvent = (eventId) => {
    setEvents(prev => prev.filter(ev => ev.id !== eventId));
    axios.delete(`${API}/user/level-events/${eventId}`, { headers: getAuthHeaders() })
      .then(() => toast.success('Aviso eliminado'))
      .catch(() => toast.error('Error al eliminar aviso'));
  };

  const persist = useCallback((next) => {
    setLevels(next);
    axios.put(`${API}/user/marked-levels`, { marked_levels: next }, { headers: getAuthHeaders() })
      .catch(() => toast.error('Error al guardar'));
  }, []);

  const saveNote = (ticker) => {
    persist(levels.map(l => l.ticker === ticker ? { ...l, note: noteDraft.trim() } : l));
    setEditingTicker(null);
    toast.success('Nota guardada');
  };

  const removeLevel = (ticker) => {
    persist(levels.filter(l => l.ticker !== ticker));
    toast.success(`Nivel de ${ticker} eliminado`);
  };

  const priceInfo = (ticker) => {
    const m = enrichedMarkets.find(em => em.ticker?.toUpperCase() === ticker);
    if (!m?.priceEUR) return null;
    return m.priceEUR;
  };

  // Feb 2026: Lista ordenada según `sortBy`. Se recalcula solo cuando cambian
  // niveles, criterio o precios enriquecidos (para orden por distancia).
  const sortedLevels = useMemo(() => {
    const arr = [...levels];
    if (sortBy === 'ticker') {
      arr.sort((a, b) => (a.ticker || '').localeCompare(b.ticker || ''));
    } else if (sortBy === 'distance') {
      // Ordena por proximidad al precio actual (distancia absoluta % ascendente).
      // Niveles sin precio conocido se envían al final.
      const distFor = (l) => {
        const cur = priceInfo(l.ticker);
        if (!cur) return Number.POSITIVE_INFINITY;
        return Math.abs(((l.level - cur) / cur) * 100);
      };
      arr.sort((a, b) => distFor(a) - distFor(b));
    } else {
      // 'date' — más reciente primero. Niveles sin timestamp al final.
      arr.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
    }
    return arr;
  }, [levels, sortBy, enrichedMarkets]);

  return (
    <div data-testid="marked-levels-panel" className="h-full flex flex-col bg-gradient-to-b from-zinc-950 to-black text-white overflow-hidden">
      {/* Cabecera */}
      <div className="px-5 pt-4 pb-3 border-b border-zinc-500/20">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(217,70,239,0.15)', border: '1px solid rgba(217,70,239,0.4)' }}>
            <Crosshair size={20} color="#e879f9" weight="bold" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-wide">ÓRDENES CONDICIONADAS</h2>
            <p className="text-xs text-zinc-500">Stop loss / Take profit · marca un nivel con clic en el gráfico · aviso 24/7 por email y rótulo al cruzarse</p>
          </div>
          <span className="ml-auto text-xs font-mono px-2.5 py-1 rounded-full" style={{ background: 'rgba(217,70,239,0.12)', color: '#e879f9', border: '1px solid rgba(217,70,239,0.3)' }}>
            {levels.length} {levels.length === 1 ? 'nivel' : 'niveles'}
          </span>
        </div>
        {/* Feb 2026: Selector de ordenación */}
        {levels.length > 1 && (
          <div className="mt-3 flex items-center gap-2">
            <SortAscending size={14} color="#a1a1aa" weight="bold" />
            <span className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Ordenar por</span>
            <select
              data-testid="marked-levels-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-[11px] font-mono bg-black/60 border border-zinc-500/30 rounded-md px-2 py-1 text-white focus:outline-none focus:border-zinc-400 cursor-pointer hover:border-zinc-400/60 transition-colors"
            >
              <option value="date">Fecha (más reciente)</option>
              <option value="ticker">Ticker (A → Z)</option>
              <option value="distance">Distancia al precio actual</option>
            </select>
          </div>
        )}
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {loading && <p className="text-sm text-zinc-500 text-center py-8">Cargando niveles...</p>}
        {!loading && levels.length === 0 && (
          <div className="text-center py-12">
            <Crosshair size={40} color="#52525b" className="mx-auto mb-3" />
            <p className="text-sm text-zinc-400">Aún no tienes órdenes condicionadas</p>
            <p className="text-xs text-zinc-600 mt-1">Haz clic sobre cualquier punto del gráfico de velas para fijar un nivel (SL/TP)</p>
          </div>
        )}
        {sortedLevels.map((l) => {
          const current = priceInfo(l.ticker);
          const dist = current ? ((l.level - current) / current) * 100 : null;
          const isEditing = editingTicker === l.ticker;
          return (
            <div
              key={l.ticker}
              data-testid={`marked-level-row-${l.ticker}`}
              className="group rounded-xl px-4 py-3 transition-colors"
              style={{ background: 'rgba(24,24,27,0.7)', border: '1px solid rgba(217,70,239,0.15)', backdropFilter: 'blur(12px)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-1 self-stretch rounded-full" style={{ background: '#d946ef' }} />
                <div className="min-w-[90px]">
                  <div className="font-bold text-sm tracking-wide">{l.ticker}</div>
                  {current && (
                    <div className="text-[11px] font-mono text-zinc-500">Actual: {current.toFixed(2)}€</div>
                  )}
                </div>
                <div className="font-mono font-bold text-base" style={{ color: '#e879f9' }}>{Number(l.level).toFixed(2)}€</div>
                {/* Badges kind/action/impact (feb 2026): intención del nivel marcado */}
                {(l.kind || l.action || l.impact === 'high') && (
                  <div className="flex items-center gap-1 flex-wrap">
                    {l.kind === 'support' && (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-500/50 bg-emerald-500/15 text-emerald-300">
                        SOP
                      </span>
                    )}
                    {l.kind === 'resistance' && (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-red-500/50 bg-red-500/15 text-red-300">
                        RES
                      </span>
                    )}
                    {l.action === 'buy' && (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                        Compra
                      </span>
                    )}
                    {l.action === 'sell' && (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-400">
                        Venta
                      </span>
                    )}
                    {l.impact === 'high' && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-gold-500/60 bg-gold-500/15 text-gold-300"
                        style={{ textShadow: '0 0 6px rgba(251,191,36,0.5)' }}
                        title="Alto impacto: emails al cruzar"
                      >
                        ⚡ ALTO
                      </span>
                    )}
                  </div>
                )}
                {dist !== null && (
                  <span
                    className="text-[11px] font-mono px-2 py-0.5 rounded-full"
                    style={dist >= 0
                      ? { background: 'rgba(0,255,106,0.1)', color: '#00ff6a', border: '1px solid rgba(0,255,106,0.25)' }
                      : { background: 'rgba(255,68,68,0.1)', color: '#ff4444', border: '1px solid rgba(255,68,68,0.25)' }}
                  >
                    {dist >= 0 ? '+' : ''}{dist.toFixed(2)}% {dist >= 0 ? 'por encima' : 'por debajo'}
                  </span>
                )}
                {/* Timestamp: cuánto lleva vigilándose (feb 2026) */}
                {l.created_at && (
                  <span
                    className="text-[10px] font-mono uppercase tracking-wider text-zinc-500"
                    title={new Date(l.created_at).toLocaleString('es-ES')}
                    data-testid={`created-at-${l.ticker}`}
                  >
                    ⏱ {formatRelativeTime(l.created_at)}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                  <button
                    data-testid={`goto-chart-${l.ticker}`}
                    title="Ver este activo en el gráfico principal"
                    onClick={() => { onTickerSelect?.(l.ticker); toast.info(`Mostrando ${l.ticker} en el gráfico`); }}
                    className="p-1.5 rounded-lg hover:bg-gold-500/15 transition-colors"
                  >
                    <ArrowSquareOut size={16} color="#22d3ee" />
                  </button>
                  <button
                    data-testid={`edit-note-${l.ticker}`}
                    title="Añadir o editar la nota de este nivel (ej: soporte fuerte, zona de compra)"
                    onClick={() => { setEditingTicker(l.ticker); setNoteDraft(l.note || ''); }}
                    className="p-1.5 rounded-lg hover:bg-zinc-500/15 transition-colors"
                  >
                    <NotePencil size={16} color="#e879f9" />
                  </button>
                  <button
                    data-testid={`delete-level-${l.ticker}`}
                    title="Eliminar este nivel: desaparece del gráfico y deja de vigilarse"
                    onClick={() => removeLevel(l.ticker)}
                    className="p-1.5 rounded-lg hover:bg-red-500/15 transition-colors"
                  >
                    <Trash size={16} color="#ff4444" />
                  </button>
                </div>
              </div>

              {/* Nota */}
              {isEditing ? (
                <div className="mt-2.5 ml-4 flex items-center gap-2">
                  <input
                    data-testid={`note-input-${l.ticker}`}
                    autoFocus
                    value={noteDraft}
                    maxLength={280}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveNote(l.ticker); if (e.key === 'Escape') setEditingTicker(null); }}
                    placeholder="Escribe una nota (ej: soporte fuerte, zona de compra...)"
                    className="flex-1 bg-black/60 border border-zinc-500/30 rounded-lg px-3 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-400"
                  />
                  <button data-testid={`save-note-${l.ticker}`} onClick={() => saveNote(l.ticker)} className="p-1.5 rounded-lg bg-zinc-500/20 hover:bg-zinc-500/30 transition-colors">
                    <Check size={14} color="#e879f9" weight="bold" />
                  </button>
                  <button onClick={() => setEditingTicker(null)} className="p-1.5 rounded-lg hover:bg-zinc-700/50 transition-colors">
                    <X size={14} color="#a1a1aa" />
                  </button>
                </div>
              ) : (
                l.note && (
                  <p className="mt-2 ml-4 text-xs text-zinc-400 italic border-l-2 pl-2.5" style={{ borderColor: 'rgba(217,70,239,0.4)' }}>
                    {l.note}
                  </p>
                )
              )}
            </div>
          );
        })}

        {/* Avisos de cruce (persisten hasta que el usuario los borre) */}
        {!loading && events.length > 0 && (
          <div className="pt-4">
            <div className="flex items-center gap-2 mb-2 px-1">
              <BellRinging size={16} color="#e879f9" weight="bold" />
              <h3 className="text-xs font-bold tracking-widest text-zinc-300">AVISOS DE CRUCE</h3>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full" style={{ background: 'rgba(217,70,239,0.12)', color: '#e879f9', border: '1px solid rgba(217,70,239,0.3)' }}>
                {events.length}
              </span>
            </div>
            <div className="space-y-1.5">
              {events.map(ev => {
                const up = ev.direction === 'up';
                const accent = up ? '#00ff6a' : '#ff4444';
                return (
                  <div
                    key={ev.id}
                    data-testid={`level-event-row-${ev.ticker}`}
                    className="group flex items-center gap-3 rounded-lg px-3 py-2"
                    style={{ background: 'rgba(24,24,27,0.5)', border: `1px solid ${up ? 'rgba(0,255,106,0.15)' : 'rgba(255,68,68,0.15)'}` }}
                  >
                    {up ? <TrendUp size={16} color={accent} weight="bold" /> : <TrendDown size={16} color={accent} weight="bold" />}
                    <button
                      onClick={() => onTickerSelect?.(ev.ticker)}
                      className="font-bold text-xs text-white hover:text-zinc-300 transition-colors"
                    >
                      {ev.ticker}
                    </button>
                    <span className="text-[11px] font-mono" style={{ color: accent }}>
                      {up ? 'superó' : 'perdió'} {Number(ev.level).toFixed(2)}€
                    </span>
                    <span className="text-[11px] font-mono text-zinc-500">a {Number(ev.price).toFixed(2)}€</span>
                    {ev.note && <span className="text-[10px] text-zinc-500 italic truncate max-w-[140px]">{ev.note}</span>}
                    <span className="ml-auto text-[10px] text-zinc-600 font-mono shrink-0">
                      {new Date(ev.triggered_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <button
                      data-testid={`delete-event-${ev.ticker}`}
                      title="Borrar aviso"
                      onClick={() => deleteEvent(ev.id)}
                      className="p-1 rounded-lg hover:bg-red-500/15 transition-colors opacity-60 group-hover:opacity-100"
                    >
                      <Trash size={14} color="#ff4444" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkedLevelsPanel;
